import { geoMercator, geoPath } from 'd3-geo'
import { css } from 'emotion'
import { motion } from 'framer-motion'
import * as React from 'react'
import { white } from '../../materials'
import { BallotFeature, BallotFeatureCollection, GeodataFeature, useGeodata } from './use-geodata'
import * as F from 'fp-ts'

// -----------------------------------------------------------------------------
// FeaturesLoader

export type FeaturesLoaderProps = Omit<FeaturesProps, 'featureCollection'> & {
  year: number
  feature: GeodataFeature
  featurePredicate?: F.function.Predicate<BallotFeature>
}

export const FeaturesLoader = React.memo<FeaturesLoaderProps>(({ year, feature, ...props }) => {
  const { data } = useGeodata({ year, feature })

  if (data) {
    return <Features {...props} featureCollection={data} />
  }

  return null
})

// -----------------------------------------------------------------------------
// Features

export interface FeaturesProps {
  width: number
  height: number

  featureCollection: BallotFeatureCollection
  featurePredicate?: F.function.Predicate<BallotFeature>
  activeFeature?: number
  pinned?: boolean

  fill: F.function.FunctionN<[BallotFeature], string>
  stroke: F.function.FunctionN<[BallotFeature], string>
  strokeWidth: F.function.FunctionN<[BallotFeature], number>

  onClick?: F.function.FunctionN<[number], void>
  onMouseEnter?: F.function.FunctionN<[number], void>
  onMouseLeave?: F.function.FunctionN<[number], void>
  onClearActiveFeature?: () => void
}

export const Features = React.memo(
  ({
    featureCollection,
    activeFeature,
    width,
    height,
    pinned,
    featurePredicate = F.function.constTrue,
    onClearActiveFeature,
    ...props
  }: FeaturesProps) => {
    const [features, pathGenerator] = React.useMemo(() => {
      const features = F.array.filter(featurePredicate)(featureCollection.features)
      const projection = geoMercator().fitSize([width, height], {
        ...featureCollection,
        features,
      } as any)
      return F.function.tuple(
        features,
        geoPath().projection(projection) as unknown as F.function.FunctionN<[BallotFeature], any>,
      )
    }, [width, height, featureCollection, featurePredicate])

    // Filter out the active feature (if any) ...
    const activeFeatureData = F.function.pipe(
      F.option.fromNullable(activeFeature),
      F.option.chain((id) =>
        F.function.pipe(
          features,
          F.array.findFirst((x) => x.id === id),
        ),
      ),
    )

    return (
      <>
        <BaseLayer features={features} {...props} pathGenerator={pathGenerator} />
        {F.function.pipe(
          activeFeatureData,
          F.option.map((feature) => (
            <>
              <rect
                width="100%"
                height="100%"
                fill="white"
                fillOpacity={0.6}
                onClick={onClearActiveFeature}
                style={{
                  pointerEvents: pinned ? 'auto' : 'none',
                  transition: '.8s opacity',
                  opacity: pinned ? 1 : 0,
                }}
              />
              <Path
                {...props}
                isActive={true}
                feature={feature}
                pathGenerator={pathGenerator}
                stroke={F.function.constant(white)}
              />
            </>
          )),
          F.option.toNullable,
        )}
      </>
    )
  },
)

interface BaseLayerProps {
  features: BallotFeature[]

  fill: F.function.FunctionN<[BallotFeature], string>
  stroke: F.function.FunctionN<[BallotFeature], string>
  strokeWidth: F.function.FunctionN<[BallotFeature], number>

  onClick?: F.function.FunctionN<[number], void>
  onMouseEnter?: F.function.FunctionN<[number], void>
  onMouseLeave?: F.function.FunctionN<[number], void>

  pathGenerator: F.function.FunctionN<[BallotFeature], any>
}

export const BaseLayer = React.memo<BaseLayerProps>(({ features, ...props }) => (
  <>
    {features.map((feature, i) => (
      <Path key={i} feature={feature} {...props} />
    ))}
  </>
))

// -----------------------------------------------------------------------------
// Path

interface PathProps {
  // Pass pathGenerator as function because we can assume the function
  // will not change.
  feature: BallotFeature
  pathGenerator: F.function.FunctionN<[BallotFeature], any>

  onClick?: F.function.FunctionN<[number], void>
  onMouseEnter?: F.function.FunctionN<[number], void>
  onMouseLeave?: F.function.FunctionN<[number], void>
  isActive?: boolean

  // Rest of the props are propagated directly to the underlying <path>
  fill: F.function.FunctionN<[BallotFeature], string>
  stroke: F.function.FunctionN<[BallotFeature], string | undefined>
  strokeWidth: F.function.FunctionN<[BallotFeature], number | string>
}

// How much the touch point can move between start and end so that we still
// recognize it as a MouseEnter (request to activate the feature).
const MAX_TOUCH_MOVE_DISTANCE = 30

export class Path extends React.PureComponent<PathProps> {
  touchStartAt: undefined | { x: number; y: number } = undefined

  onTouchStart = (ev: React.TouchEvent<SVGPathElement>): void => {
    this.touchStartAt = { x: ev.touches[0].screenX, y: ev.touches[0].screenY }

    // To avoid de-activating the feature. There's a onTouchStart event handler attached
    // to one of our parents which deselects the canton.
    ev.stopPropagation()
  }

  onTouchEnd = (ev: React.TouchEvent<SVGPathElement>) => {
    if (this.touchStartAt) {
      const { x: sX, y: sY } = this.touchStartAt
      const { x: dX, y: dY } = { x: ev.changedTouches[0].screenX, y: ev.changedTouches[0].screenY }
      const distance = Math.sqrt(Math.pow(sX - dX, 2) + Math.pow(sY - dY, 2))
      if (distance < MAX_TOUCH_MOVE_DISTANCE) {
        this.onMouseEnter()
      }

      this.touchStartAt = undefined
    }
  }

  onMouseEnter = (): void => {
    const {
      onMouseEnter,
      feature: { id },
    } = this.props
    if (onMouseEnter && typeof id === 'number') {
      onMouseEnter(id)
    }
  }

  onClick = (): void => {
    const {
      onClick,
      feature: { id },
    } = this.props
    if (onClick && typeof id === 'number') {
      onClick(id)
    }
  }

  onMouseLeave = (): void => {
    const {
      onMouseLeave,
      feature: { id },
    } = this.props
    if (onMouseLeave && typeof id === 'number') {
      onMouseLeave(id)
    }
  }

  render() {
    const { isActive, feature, fill, stroke, strokeWidth, pathGenerator } = this.props
    const isInteractive = this.props.onMouseEnter || this.props.onMouseLeave || this.props.onClick

    return (
      <>
        {isActive && (
          <motion.path
            key={feature.id}
            strokeLinejoin="round"
            fill="black"
            fillOpacity={0.4}
            stroke="none"
            d={pathGenerator(feature) || undefined}
            className={pathClassName}
            pointerEvents="none"
            initial={{ opacity: 0, translateY: 5, scale: 0.8 }}
            animate={{ opacity: 1, translateY: 10, scale: 0.9 }}
            style={{
              filter: `url(#blur)`,
            }}
          />
        )}

        <path
          fill={fill(feature)}
          stroke={stroke(feature)}
          strokeLinejoin="round"
          strokeWidth={strokeWidth(feature)}
          d={pathGenerator(feature) || undefined}
          onTouchStart={this.onTouchStart}
          onTouchEnd={this.onTouchEnd}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
          onClick={this.onClick}
          className={pathClassName}
          pointerEvents={isActive || !isInteractive ? 'none' : 'all'}
          vectorEffect="non-scaling-stroke"
          style={{
            //filter: isActive ? `url(#dropshadow)` : undefined,
            cursor: isInteractive ? 'pointer' : 'default',
          }}
        />
      </>
    )
  }
}

const pathClassName = css`
  -webkit-tap-highlight-color: transparent;

  -ms-touch-action: manipulation;
  touch-action: manipulation;
`
